import { SearchRegistry } from '../types';

export const rydFinancialSearchEntries: SearchRegistry = {
  '/accounting/invoicing/e-invoice-validation': {
    keywords: [
      'e-invoice',
      'einvoice',
      'e invoice validation',
      'factur-x',
      'facturx',
      'zugferd',
      'en 16931',
      'xrechnung',
      'elektronische rechnung',
      'e-rechnung',
      'embedded xml',
      'invoice xml validation',
      'pdf/a-3',
    ],
    questions: [
      'How do I validate the e-invoice data of an invoice?',
      'Where can I check the embedded factur-x.xml of an invoice PDF?',
      'Which invoices exist for an order UUID and are they valid e-invoices?',
      'How do I inspect the EN 16931 fields of an uploaded invoice PDF?',
    ],
    faqs: [
      {
        q: 'How do I validate the e-invoice (Factur-X) data of an invoice?',
        a: 'Open ryd Financial → Invoicing → E-Invoice Validation. Enter the order UUID to find its invoice PDFs in Odoo, or upload an invoice PDF. The page extracts the embedded factur-x.xml in your browser, checks the EN 16931 profile, mandatory BT fields, BR-27 and BR-CO reconciliation rules, and shows every e-invoice field with its value.',
      },
    ],
  },
  '/accounting/invoicing/e-invoice-repair': {
    keywords: [
      'e-invoice repair',
      'einvoice repair',
      'correct e-invoice',
      'fix factur-x',
      'repair factur-x',
      'wrong embedded xml',
      'corrected invoice pdf',
      'e-rechnung korrigieren',
    ],
    questions: [
      'How do I correct the embedded e-invoice data of an invoice?',
      'The PDF of an invoice is right but its factur-x.xml is wrong — how do I fix it?',
      'How do I download a corrected Factur-X invoice PDF?',
    ],
    faqs: [
      {
        q: 'How do I correct wrong embedded e-invoice (Factur-X) data?',
        a: 'Open ryd Financial → Invoicing → E-Invoice Repair. Enter the order UUID or upload the invoice PDF. The backend takes the factur-x.xml from the Odoo e-invoice generator (falling back to a repair of the embedded XML when the Odoo XML does not match the invoice) and re-embeds it without changing the visible pages; the page then validates the original and the corrected copy side by side and lets you download the corrected PDF. Nothing is written to Odoo.',
      },
    ],
  },
  '/accounting/reporting/b2c-invoices': {
    keywords: [
      'b2c invoices',
      'b2c report',
      'invoice report',
      'country report',
      'product type report',
      'monthly invoice report',
      'cancel invoices',
      'credit notes',
      'station country',
      'finance csv',
      'rechnungsbericht',
    ],
    questions: [
      'How do I generate the monthly B2C invoices report?',
      'Where can I download B2C invoice counts and amounts by country and product?',
      'How many B2C invoices and cancel invoices were created last month per country?',
    ],
    faqs: [
      {
        q: 'How do I generate the monthly B2C invoices report by country and product type?',
        a: 'Open ryd Financial → Reporting → B2C Invoices (requires the accountingAdmin role). Pick the month (and optionally a station country or product types), press Generate Report, then Download CSV. Odoo aggregates posted B2C invoices and credit notes (business type PAY_4_GAS, which includes B2B2C consumer invoices of API partners and excludes fleet invoices) by product category and station country.',
      },
    ],
  },
  '/accounting/overview': {
    keywords: [
      'ryd financial',
      'accounting',
      'finance',
      'finance overview',
      'accounting overview',
      'financial dashboard',
      'finanz',
      'buchhaltung',
      'finance landing',
      'accounting landing',
    ],
    questions: [
      'Where is the ryd Financial / accounting overview?',
      'How do I open the financial dashboard?',
    ],
  },
  '/accounting/invoicing/failed-requests': {
    keywords: [
      'invoicing failed requests',
      'failed invoices',
      'invoice errors',
      'invoicing errors',
      'invoice queue',
      'rechnungsfehler',
      'fehlerhafte rechnungen',
      'failed invoice creation requests',
      'retry invoice',
      'retry to odoo',
      'processed status',
      'processed only',
      'unprocessed only',
      'all requests',
      'invoice generation',
      'invoice queue retry',
    ],
    questions: [
      'Which invoices failed to generate?',
      'Where do I retry a failed invoice request?',
      'Why is invoice X stuck in failed state?',
      'How do I see only unprocessed failed invoice requests?',
    ],
    faqs: [
      {
        q: 'Where do I retry an invoice request that failed to generate?',
        a: 'Open ryd Financial → Invoicing → Invoicing Failed Requests. Filter by date range, processed status (All / Processed Only / Unprocessed Only) or order UUID, then click Retry on the row to resend the invoice creation request to Odoo.',
      },
    ],
  },
  '/accounting/invoicing/data-consistency-check': {
    keywords: [
      'data consistency',
      'consistency check',
      'invoice consistency',
      'data audit',
      'reconciliation check',
      'mismatch check',
      'order vs invoice mismatch',
      'data integrity',
    ],
    questions: [
      'How do I check whether invoicing data is consistent?',
      'Where do I see invoicing data-consistency issues?',
      'Why does the consistency check flag specific orders?',
    ],
  },
  '/accounting/invoicing/odoo-order-monitor': {
    keywords: [
      'odoo order monitor',
      'odoo delay monitor',
      'odoo volume monitor',
      'odoo invoice consistency',
      'odoo delays',
      'odoo volume checks',
      'order ingestion volume',
      'order processing delay',
      'delayed orders',
      'stuck queue jobs',
      'failed queue jobs',
      'queue job monitor',
      'mercedes alerts',
      'mercedes order delay',
      'erp monitoring',
      'odoo monitoring',
      'sale order delay',
    ],
    questions: [
      'Where do I see delayed Odoo orders?',
      'How do I check whether order processing in Odoo is stuck?',
      'How do I trigger a manual Odoo delay check?',
      'How do I trigger a manual Odoo volume check?',
      'Where do I see the history of Odoo delay checks?',
    ],
    faqs: [
      {
        q: 'How do I find out why an Odoo delay alert was posted to Slack?',
        a: 'Open ryd Financial → Invoicing → Odoo Order Monitor. Each check run lists the delayed queue jobs and sales orders it found; click a row to see the affected Odoo records. The Slack message also links directly back to this page.',
      },
    ],
  },
  '/accounting/invoicing/invoice-cancellation': {
    keywords: [
      'invoice cancellation',
      'cancel invoice',
      'storno',
      'rechnung stornieren',
      'invoice void',
      'invoice reversal',
      'cancel order/invoice',
      'mark order cancelled',
      'cleanup operations',
      'odoo cancellation',
    ],
    questions: [
      'How do I cancel an invoice?',
      'Where can support cancel a wrong invoice?',
      'When is a Storno invoice generated?',
      'Does cancelling an invoice also cancel the underlying order?',
    ],
    faqs: [
      {
        q: 'How do I cancel an incorrectly generated invoice?',
        a: 'Open ryd Financial → Invoicing → Invoice Cancellation, paste the order UUID and run Search Order. The page lets you cancel the order and invoice together — the order is marked cancelled and any necessary cleanup operations are performed in Odoo.',
      },
    ],
  },
  '/accounting/invoicing/invoice-modification': {
    keywords: [
      'invoice modification',
      'modify invoice',
      'edit invoice',
      'rechnung ändern',
      'invoice correction',
      'amend invoice',
      'correct invoice line',
      'fix invoice amount',
      'invoice rewrite',
    ],
    questions: [
      'How do I modify an invoice?',
      'Where do I correct an invoice line as customer support?',
    ],
  },
  '/accounting/partner-file-hub/q8': {
    keywords: [
      'q8 reconciliation',
      'q8',
      'partner file hub q8',
      'q8 file',
      'q8 reconciliation report',
      'kuwait petroleum',
      'q8 exchange files',
      'q8 sftp',
      'q8 file ingest',
    ],
    questions: [
      'Where do I see Q8 reconciliation files?',
      'How is Q8 partner reconciliation processed?',
      'How are new Q8 files picked up?',
    ],
  },
  '/accounting/partner-file-hub/tamoil': {
    keywords: [
      'tamoil',
      'tamoil italy',
      'tamoil reconciliation',
      'partner file hub tamoil',
      'italy reconciliation',
      'tamoil reconciliation process',
      'tamoil exchange files',
    ],
    questions: [
      'Where do I see Tamoil Italy reconciliation files?',
      'How does the Tamoil Italy reconciliation work?',
    ],
  },
  '/accounting/eg': {
    keywords: [
      'eg reconciliation',
      'eg',
      'eg group',
      'eg fuel',
      'eg partner reconciliation',
      'eg reconciliation report',
      'eg uk',
      'eg tankstellen',
    ],
    questions: [
      'How does EG partner reconciliation work?',
      'Where do I see EG reconciliation reports?',
    ],
  },
  '/accounting/list-price-reports': {
    keywords: [
      'list price reports',
      'price reports',
      'list-price audit',
      'price reporting',
      'price report download',
      'monthly list price report',
      'price report export',
    ],
    questions: [
      'Where do I see and export the list-price reports?',
      'How do I download the monthly list-price report?',
    ],
  },
  '/accounting/fiscal-monitoring/poland': {
    keywords: [
      'poland fiscal monitoring',
      'exorigo upos',
      'exorigo-upos',
      'fiscal printer poland',
      'poland fiscal receipt',
      'poland outbound logs',
      'poland inbound callbacks',
      'fiscal monitoring poland',
      'partner fiscal monitoring',
    ],
    questions: [
      'Where do I inspect Poland fiscal printer API logs?',
      'How do I see Exorigo-Upos outbound and inbound callbacks for an order?',
      'Where is the Poland fiscal order timeline?',
    ],
  },
  '/accounting/fiscal-monitoring/poland/resend': {
    keywords: [
      'poland smartorder resend',
      'resend smartorder request',
      'resend fiscal request poland',
      'exorigo upos resend',
      'resend by order uuid',
      'poland fiscal resend',
      'latest smartorder request',
    ],
    questions: [
      'How do I resend a Poland SmartOrder request when I only have the order UUID?',
      'Where do I find the latest SmartOrder request for an order?',
      'How do I deeplink to the Poland fiscal resend for an order?',
    ],
  },
  '/accounting/fiscal-monitoring/moloni': {
    keywords: [
      'moloni fiscal monitoring',
      'moloni portugal',
      'portugal fiscal monitoring',
      'portuguese invoice logs',
      'moloni invoice sequence',
      'moloni credit note',
      'partner fiscal monitoring',
      'fiscal monitoring portugal',
    ],
    questions: [
      'Where do I inspect Moloni fiscal API logs for Portugal?',
      'How do I see Portuguese invoice attempts and credit notes for an order?',
      'Where is the Moloni operation timeline?',
    ],
  },
};
